import React, { FC, useCallback, useMemo, useState } from 'react';
import {
  faEdit,
  faTrash,
  Icon,
  CustomColumns,
  CustomTable,
} from '@/ui/components';
import { AppUserTypes, Inspector, User } from '@/types';
import {
  useUsers,
  useUserTypes,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
} from '@/api/Users';
import { App, Button, Space, Switch, Tag } from 'antd';
import { useTranslate } from '@/translations';
import {
  useCustomersHierarchy,
  useCustomerInspectors,
  useCreateInspector,
} from '@/api/Customers';
import { useLocations } from '@/api/Locations';
import { UserPopup } from '../UserPopup';
import { InspectorPopup } from '../InspectorPopup';

export const ManageUsersDataGrid: FC = () => {
  const { t } = useTranslate();
  const { modal } = App.useApp();

  const { data: userData, isFetching: isUserLoading } = useUsers();
  const { data: userTypesData, isFetching: isUserTypesLoading } =
    useUserTypes();
  const { data: customersHierarchy, isFetching: isCustomersHierarchyLoading } =
    useCustomersHierarchy();
  const { data: customerData, isFetching: customersLoading } =
    useCustomerInspectors();
  const { data: locationData, isFetching: isLocationsLoading } = useLocations();
  const { mutateAsync: createMutateAsync } = useCreateUser();
  const { mutateAsync: updateMutateAsync } = useUpdateUser();
  const { mutateAsync: deleteMutateAsync, isPending: isDeleteLoading } =
    useDeleteUser();
  const { mutateAsync: createInspectorAsync } = useCreateInspector();

  const [user, setUser] = useState<User>();
  const [showCreateInspector, setShowCreateInspector] = useState(false);

  const locations = locationData?.filter(
    (o) => o.locationTypeId === 8 && o.isActive,
  );
  const isLoading =
    isUserLoading ||
    isUserTypesLoading ||
    isCustomersHierarchyLoading ||
    customersLoading ||
    isLocationsLoading ||
    isDeleteLoading;

  const defaultUserData: User = {
    id: 0,
    displayName: '',
    userName: '',
    userType: AppUserTypes.InternalUsers,
    roles: [],
    isTmsUser: false,
    customers: [],
    receiveEmail: true,
  };

  const onSubmitUser = async (data: User) => {
    const res = await (data.id
      ? updateMutateAsync(data)
      : createMutateAsync(data));
    return res.message?.Message;
  };

  const onDeleteUser = useCallback(
    (id: number) => {
      modal.confirm({
        title: 'Confirm',
        content: t('deleteRecordQuestion'),
        okText: t('yes'),
        onOk: () => deleteMutateAsync({ userId: id }),
        onCancel: () => {},
      });
    },
    [deleteMutateAsync, modal, t],
  );

  const onSubmitInspector = async (data: Inspector) => {
    const res = await createInspectorAsync(data);
    return res.message?.Message;
  };

  const columns: CustomColumns<User> = useMemo(
    () => [
      {
        dataIndex: 'userName',
        title: t('username'),
        sortable: true,
        filter: true,
        width: 200,
        fixed: 'left',
      },
      {
        dataIndex: 'displayName',
        title: t('displayName'),
        sortable: true,
        filter: true,
        width: 180,
        defaultSortOrder: 'ascend',
      },
      {
        dataIndex: 'userTypeName',
        title: t('userType'),
        sortable: true,
        filter: true,
        width: 100,
      },
      {
        dataIndex: 'roleNames',
        title: t('userRoles'),
        filter: true,
        onFilter: (value, record) =>
          record.roleNames?.some((o) =>
            o.toLowerCase().includes((value as string).toLowerCase()),
          ) || false,
        width: 150,
        render: (_, data) => (
          <Space wrap>
            {data.roleNames?.map((o) => (
              <Tag key={o} className="py-1 m-0">
                {o}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        dataIndex: 'isTmsUser',
        title: t('tmsAccess'),
        renderType: 'select',
        filter: true,
        filterOptions: [
          {
            text: 'Yes',
            value: true,
          },
          {
            text: 'No',
            value: false,
          },
        ],
        onFilter: (value, record) => record.isTmsUser === (value as boolean),
        width: 100,
        render: (value) => <Switch checked={value} />,
      },
      {
        dataIndex: 'customers',
        title: 'Customers',
        width: 150,
        filter: true,
        onFilter: (value, record) =>
          record.customers.some((o) =>
            o.customerName
              ?.toLowerCase()
              .includes((value as string).toLowerCase()),
          ),
        render: (_, data) => (
          <Space wrap>
            {data.customers.map((o) => (
              <Tag key={o.customerId} className="py-1 m-0 text-wrap">
                {o.customerName}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        dataIndex: 'locations',
        title: 'Sites',
        width: 250,
        filter: true,
        onFilter: (value, record) =>
          record.locations?.some((o) =>
            o.text?.toLowerCase().includes((value as string).toLowerCase()),
          ) || false,
        render: (_, data) => (
          <Space wrap>
            {data.locations?.map((o) => (
              <Tag key={o.value} className="py-1 m-0 text-wrap">
                {o.text}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        dataIndex: 'active',
        title: 'Active',
        renderType: 'select',
        filter: true,
        filterOptions: [
          {
            text: 'Yes',
            value: true,
          },
          {
            text: 'No',
            value: false,
          },
        ],
        onFilter: (value, record) => record.active === (value as boolean),
        width: 110,
        render: (value) => <Switch checked={value} />,
      },
      {
        dataIndex: 'receiveEmail',
        title: 'Receive Email',
        renderType: 'select',
        filter: true,
        filterOptions: [
          {
            text: 'Yes',
            value: true,
          },
          {
            text: 'No',
            value: false,
          },
        ],
        onFilter: (value, record) => record.receiveEmail === (value as boolean),
        width: 110,
        render: (value) => <Switch checked={value} />,
      },
      {
        dataIndex: 'id',
        render: (_, data) => (
          <Space>
            <Button size="small" onClick={() => setUser({ ...data })}>
              <Icon icon={faEdit} /> {t('edit')}
            </Button>

            <Button
              color="danger"
              variant="outlined"
              size="small"
              onClick={() => onDeleteUser(data.id)}
            >
              <Icon icon={faTrash} /> {t('delete')}
            </Button>
          </Space>
        ),
        width: 180,
        fixed: 'right',
      },
    ],
    [onDeleteUser, t],
  );

  return (
    <>
      <CustomTable
        dataSource={userData || []}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        onAdd={() => setUser({ ...defaultUserData })}
        headerActions={{
          right: (
            <Button onClick={() => setShowCreateInspector(true)}>
              Create Inspector
            </Button>
          ),
        }}
        scroll={{ x: 1300 }}
      />

      <UserPopup
        data={user}
        userTypeRoles={userTypesData}
        customers={customerData}
        customersHierarchy={customersHierarchy}
        locations={locations}
        onCancel={() => setUser(undefined)}
        onSubmit={onSubmitUser}
      />

      <InspectorPopup
        open={showCreateInspector}
        customers={customerData}
        locations={locations}
        onCancel={() => setShowCreateInspector(false)}
        onSubmit={onSubmitInspector}
      />
    </>
  );
};
